import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import { useTenant } from '@/hooks/use-tenant';
import { useNextScheduledCampaign } from '@/hooks/use-next-scheduled-campaign';
import { campaignDayLabel } from '@/lib/home-dates';
import { mobileLayer } from '@/lib/mobile-layer';
import { HomeCardLink } from '@/components/home/home-card-link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * "Próxima campanha" (HOME-06): the nearest scheduled campaign with its day,
 * time and audience, the notification preview and "Editar campanha"; or
 * "Nenhuma campanha agendada" with "Criar campanha".
 */
export function NextCampaignCard() {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const { query, campaign } = useNextScheduledCampaign();
  const base = `/${tenant.slug}/campaigns`;

  const body = () => {
    if (query.isError) {
      return (
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-muted-foreground">
            {LL.home.nextCampaign.loadError()}
          </p>
          <Button
            type="button"
            variant="outline"
            className={mobileLayer.touch}
            onClick={() => void query.refetch()}
          >
            {LL.home.nextCampaign.retry()}
          </Button>
        </div>
      );
    }
    if (campaign === undefined) {
      return (
        <>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-20 w-full" />
        </>
      );
    }
    if (campaign === null) {
      return (
        <div className="flex flex-col items-start gap-1">
          <p className="text-sm text-muted-foreground">
            {LL.home.nextCampaign.empty()}
          </p>
          <HomeCardLink to={`${base}/create`}>
            {LL.home.nextCampaign.create()}
          </HomeCardLink>
        </div>
      );
    }

    const launch = DateTime.fromISO(campaign.launchDateTime);
    return (
      <>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="break-words text-[17px] font-semibold tracking-[-0.01em]">
            {campaign.name}
          </p>
          <p className="text-sm text-muted-foreground">
            {LL.home.nextCampaign.when({
              day: campaignDayLabel(campaign.launchDateTime, locale),
              time: launch.toFormat('HH:mm'),
              audience:
                campaign.audience?.name ?? LL.home.nextCampaign.allCustomers(),
            })}
          </p>
        </div>
        {/* Compact preview (as on iOS): one-line title, two-line message. */}
        <div
          data-home-notification-preview
          className="flex items-start gap-2.5 rounded-2xl bg-muted px-3 py-2.5"
        >
          <img
            src={tenant.iconUrl}
            alt=""
            className="size-[30px] shrink-0 rounded-[7px]"
          />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="flex items-baseline justify-between gap-2 text-[13px] font-semibold">
              <span className="truncate">{campaign.notificationTitle}</span>
              <span className="shrink-0 text-xs font-normal text-muted-foreground">
                {LL.notificationPreview.now()}
              </span>
            </span>
            <span className="line-clamp-2 text-[13px] leading-snug">
              {campaign.notificationBody}
            </span>
          </span>
        </div>
        <HomeCardLink
          to={`${base}/${campaign.id}`}
          className="mt-auto min-h-9 max-md:min-h-11"
        >
          {LL.home.nextCampaign.edit()}
        </HomeCardLink>
      </>
    );
  };

  return (
    <Card data-home-next-campaign className="flex flex-col">
      <CardContent className="flex flex-1 flex-col gap-2.5 p-5 max-md:p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          {LL.home.nextCampaign.title()}
        </p>
        {body()}
      </CardContent>
    </Card>
  );
}
